// Relationship Health (0–100) — one number per client, fully explained by five factors.
// Book Health = premium-weighted average across clients.
import { diffDays, isActivePolicy, isTerminalStage, isStalled, missingRequirements, reviewStatus, annualNow, type Day, type WorkspaceView } from '@vero/domain';
import { coverageProfile } from './coverage.ts';
import { lastContact } from './index.ts';

export interface HealthFactor { key: 'coverage' | 'engagement' | 'review' | 'open_items' | 'profile'; points: number; max: number; detailKey: string; params?: Record<string, string | number> }
export interface Health { score: number; band: 'strong' | 'steady' | 'watch' | 'at_risk'; factors: HealthFactor[] }

export const bandOf = (s: number): Health['band'] => (s >= 80 ? 'strong' : s >= 60 ? 'steady' : s >= 40 ? 'watch' : 'at_risk');

export function relationshipHealth(v: WorkspaceView, personId: string, today: Day): Health {
  const p = v.get('persons', personId)!;
  const cov = coverageProfile(v, personId, today);
  const coverage = Math.round((cov.score / 100) * 30);

  const lc = lastContact(v, personId);
  const since = lc ? diffDays(lc.at.slice(0, 10), today) : null;
  const engagement = since === null ? 5 : since <= 90 ? 25 : since <= 180 ? 18 : since <= 365 ? 10 : 3;

  const rs = reviewStatus(p, v.where('policies', 'personId', personId), today);
  const review = rs.state === 'ok' ? 20 : rs.state === 'due_soon' ? 15 : rs.state === 'not_applicable' ? 12 : rs.state === 'never' ? 5 : Math.max(0, Math.round(8 - rs.daysOverdue / 45));

  const overdue = v.where('tasks', 'personId', personId).filter((t) => t.status === 'open' && t.dueDate && t.dueDate < today).length;
  const cases = v.where('cases', 'personId', personId).filter((c) => !isTerminalStage(c.stage));
  const stalled = cases.filter((c) => isStalled(c, today)).length;
  const missing = cases.reduce((s, c) => s + missingRequirements(v.where('case_requirements', 'caseId', c.id)).length, 0);
  const open = Math.max(0, 15 - overdue * 5 - stalled * 5 - Math.min(missing, 5));

  const fields = [p.primaryPhone, p.primaryEmail, p.birthDate, p.city || p.address, p.occupation];
  const profile = fields.filter(Boolean).length * 2;

  const factors: HealthFactor[] = [
    { key: 'coverage', points: coverage, max: 30, detailKey: 'health.detail.coverage', params: { gaps: cov.gaps.length } },
    { key: 'engagement', points: engagement, max: 25, detailKey: since === null ? 'health.detail.never' : 'health.detail.engagement', params: { days: since ?? 0 } },
    { key: 'review', points: review, max: 20, detailKey: `enum.reviewState.${rs.state}` },
    { key: 'open_items', points: open, max: 15, detailKey: 'health.detail.open', params: { overdue, stalled, missing } },
    { key: 'profile', points: profile, max: 10, detailKey: 'health.detail.profile', params: { filled: fields.filter(Boolean).length, total: fields.length } },
  ];
  const score = factors.reduce((s, f) => s + f.points, 0);
  return { score, band: bandOf(score), factors };
}

export interface BookHealth { score: number; band: Health['band']; clients: number; distribution: Record<Health['band'], number>; factors: Record<HealthFactor['key'], number> }

export function bookHealth(v: WorkspaceView, today: Day): BookHealth {
  const clients = v.rows('persons').filter((p) => p.stage === 'client' && !p.archivedAt);
  const dist: BookHealth['distribution'] = { strong: 0, steady: 0, watch: 0, at_risk: 0 };
  const fsum: BookHealth['factors'] = { coverage: 0, engagement: 0, review: 0, open_items: 0, profile: 0 };
  let wsum = 0, ssum = 0;
  for (const c of clients) {
    const h = relationshipHealth(v, c.id, today);
    const w = 1 + v.where('policies', 'personId', c.id).filter(isActivePolicy).reduce((s, x) => s + annualNow(v, x, today), 0) / 1000;
    wsum += w; ssum += h.score * w; dist[h.band]++;
    for (const f of h.factors) fsum[f.key] += (f.points / f.max) * w;
  }
  const score = wsum ? Math.round(ssum / wsum) : 0;
  const factors = Object.fromEntries(Object.entries(fsum).map(([k, x]) => [k, wsum ? Math.round((x / wsum) * 100) : 0])) as BookHealth['factors'];
  return { score, band: bandOf(score), clients: clients.length, distribution: dist, factors };
}
